<script setup lang="ts">
  import { Execution } from '@/shared/Executions/models/Execution'
  import { ExecutionEngineType } from '@/shared/Executions/models/ExecutionEngineType'
  import { IExecutionPhaseService } from '@/shared/Executions/services/ExecutionPhaseService'
  import { computed } from 'vue'

  const props = defineProps<{
    record: Execution
    index?: number
    selectable?: boolean
    onCellClick?: (record: Execution, e?: Event) => void
    executionPhaseService: IExecutionPhaseService
  }>()

  // Prefer top-level engineVersions (aggregated by backend) when available.
  // Fall back to iterating phases[].versions[] if top-level data is absent.
  // Match both old (aplos_nca_calc_engine) and new (aplos_nca_analysis_engine) package names.
  const isCalcEngine = (ev: { package?: string; name?: string }) =>
    ev.package === ExecutionEngineType.AplosNcaCalcEngine ||
    ev.name === ExecutionEngineType.AplosNcaCalcEngine ||
    ev.package === ExecutionEngineType.AplosNcaAnalysisEngine ||
    ev.name === ExecutionEngineType.AplosNcaAnalysisEngine

  const engineVersion = computed(() => {
    // Check top-level engineVersions first
    const topLevelVersions = props.record?.engineVersions ?? []
    for (const ev of topLevelVersions) {
      if (isCalcEngine(ev) && ev.version) return ev.version
    }

    // Fall back to phase-level versions
    const phases = props.record?.phases ?? []
    for (const phase of phases) {
      // Try new name first, then old name
      let ver = props.executionPhaseService.tryGetVersionforEnginePhaseByType(
        ExecutionEngineType.AplosNcaAnalysisEngine,
        phase
      )
      if (ver && ver !== 'Not Available') return ver
      ver = props.executionPhaseService.tryGetVersionforEnginePhaseByType(
        ExecutionEngineType.AplosNcaCalcEngine,
        phase
      )
      if (ver && ver !== 'Not Available') return ver
    }
    return 'Not Available'
  })

  function handleClick(e?: Event) {
    if (props.selectable && props.onCellClick) {
      props.onCellClick(props.record, e)
    }
  }
</script>

<template>
  <div :class="[{ 'cursor-pointer': selectable }]" @click="handleClick">
    <a-tooltip>
      <template #title>
        <span>
          <b>Aplos NCA Calculation Engine:</b>
          {{ engineVersion }}
        </span>
      </template>
      <span role="engine-version">{{ engineVersion }}</span>
    </a-tooltip>
  </div>
</template>
